/**
 * Delivery of the Shop Terminal closing report.
 *
 * Transport is the existing `send-email` edge function, reached through
 * `sendEdgeEmail`. Nothing new is deployed for this: that function already
 * resolves the tenant's Brevo credentials, rate limits, and accepts a base64
 * PDF attachment, and reusing it keeps the edge-function surface unchanged.
 */

import { supabase } from './supabase';
import { sendEdgeEmail } from './b2bEmail';
import { htmlToPdfBase64 } from './pdfGenerator';
import { createContextLogger } from './logger';
import { summariseShopReceipt, type ShopReceiptReportLine } from '../components/shop-terminal/shop-receipt-report';

const log = createContextLogger('ShopReceiptEmail');

export interface WorkerContactOption {
  id: string;
  name: string;
  email: string;
  job_function: string | null;
}

/**
 * True when this tenant can actually send mail.
 *
 * Brevo credentials are per tenant, and on this database several companies have
 * none at all. Probing up front lets the terminal present Print as the primary
 * action instead of letting a worker tap Send and hit a failure he cannot fix.
 */
export async function isEmailConfigured(adminId: string): Promise<boolean> {
  if (!adminId) return false;

  const { data, error } = await supabase
    .from('admin_brevo_credentials')
    .select('id')
    .eq('admin_id', adminId)
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    // Cannot prove it is unusable, so do not hide the option on a read error.
    log.warn('Could not check email configuration', { adminId, error: error.message });
    return true;
  }

  return Boolean(data);
}

/** The tenant's worker directory, offered as a secondary recipient choice. */
export async function fetchWorkerContacts(adminId: string): Promise<WorkerContactOption[]> {
  if (!adminId) return [];

  const { data, error } = await supabase
    .from('worker_contacts')
    .select('id, name, email, job_function')
    .eq('admin_id', adminId)
    .order('name');

  if (error) {
    log.warn('Could not load worker contacts', { adminId, error: error.message });
    return [];
  }

  return (data as WorkerContactOption[]) ?? [];
}

/**
 * Who hears about a discrepancy.
 *
 * The configured list is `shop_notification_emails`, the same table the
 * replenishment pick list uses. On this database only one company has ever
 * filled it in, so without a fallback the alert would silently reach nobody for
 * almost every tenant. The tenant admin's own address is the backstop, which
 * means the alert works on day one with zero configuration and the setting
 * still takes over once somebody fills it in.
 */
async function resolveAdminAlertRecipients(adminId: string): Promise<string[]> {
  const { data: configured } = await supabase
    .from('shop_notification_emails')
    .select('email')
    .eq('admin_id', adminId)
    .eq('is_active', true);

  const emails = (configured ?? [])
    .map((row) => (row as { email: string }).email)
    .filter((email): email is string => Boolean(email && email.includes('@')));

  if (emails.length > 0) return emails;

  const { data: admin } = await supabase
    .from('users')
    .select('email')
    .eq('id', adminId)
    .maybeSingle();

  const fallback = (admin as { email?: string } | null)?.email;
  return fallback && fallback.includes('@') ? [fallback] : [];
}

interface ReceiptSummaryForEmail {
  reservationNumber: string;
  customerName?: string | null;
  confirmedByName?: string | null;
  lines: ShopReceiptReportLine[];
}

function buildEmailBody(summary: ReceiptSummaryForEmail, intro: string): string {
  const totals = summariseShopReceipt(summary.lines);

  const tile = (label: string, value: number, colour: string, bg: string, border: string) => `
    <td style="padding:0 4px;width:33.33%;">
      <div style="background:${bg};border:1px solid ${border};border-radius:8px;padding:12px;text-align:center;">
        <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;font-weight:700;color:${colour};">${label}</div>
        <div style="font-size:26px;font-weight:800;color:${colour};line-height:1.1;">${value}</div>
      </div>
    </td>`;

  const listOf = (lines: ShopReceiptReportLine[], qty: (line: ShopReceiptReportLine) => number) =>
    lines.length === 0
      ? '<li style="color:#9ca3af;">None</li>'
      : lines
          .map((line) => {
            const variant = [line.color, line.size].filter(Boolean).join(' / ');
            const name = line.product_name || line.sku || 'Item';
            return `<li>${qty(line)} &times; ${name}${variant ? ` (${variant})` : ''}${
              line.sku ? ` &mdash; <code>${line.sku}</code>` : ''
            }</li>`;
          })
          .join('');

  return `
    <div style="font-family:Arial,sans-serif;max-width:680px;margin:0 auto;color:#1f2937;">
      <div style="background:#0e7490;color:#fff;padding:22px 28px;border-radius:8px 8px 0 0;">
        <h1 style="margin:0;font-size:21px;">Shop Receipt</h1>
        <p style="margin:6px 0 0;opacity:0.9;font-size:14px;">Reservation ${summary.reservationNumber}</p>
      </div>
      <div style="padding:26px 28px;background:#fff;border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;">
        <p style="margin:0 0 18px;">${intro}</p>

        <table style="width:100%;border-collapse:separate;border-spacing:0;margin-bottom:20px;">
          <tr>
            ${tile('Received', totals.receivedUnits, '#047857', '#ecfdf5', '#a7f3d0')}
            ${tile('Missing', totals.missingUnits, '#b45309', '#fffbeb', '#fde68a')}
            ${tile('Extra', totals.extraUnits, '#6d28d9', '#f5f3ff', '#ddd6fe')}
          </tr>
        </table>

        <h3 style="margin:0 0 6px;font-size:13px;color:#b45309;text-transform:uppercase;letter-spacing:0.04em;">Missing</h3>
        <ul style="margin:0 0 16px;padding-left:20px;font-size:14px;">${listOf(
          totals.missingLines,
          (l) => (l.missing_qty > 0 ? l.missing_qty : Math.max(l.original_qty - l.received_qty, 0))
        )}</ul>

        <h3 style="margin:0 0 6px;font-size:13px;color:#6d28d9;text-transform:uppercase;letter-spacing:0.04em;">Extra</h3>
        <ul style="margin:0 0 20px;padding-left:20px;font-size:14px;">${listOf(
          totals.extraLines,
          (l) => l.received_qty
        )}</ul>

        <div style="background:#f9fafb;border-left:4px solid #0891b2;padding:12px 16px;border-radius:0 8px 8px 0;">
          <p style="margin:0;font-size:13px;color:#374151;">
            The full report is attached as a PDF. This is a record of what the shop counted &mdash;
            <strong>no stock was added or removed</strong> by this confirmation.
          </p>
        </div>
      </div>
    </div>`;
}

export interface SendShopReceiptReportArgs {
  adminId: string;
  to: string;
  reportHtml: string;
  summary: ReceiptSummaryForEmail;
}

/** Emails the closing report to the worker, with the full document as a PDF. */
export async function sendShopReceiptReport({
  adminId,
  to,
  reportHtml,
  summary,
}: SendShopReceiptReportArgs): Promise<void> {
  const pdfBase64 = await htmlToPdfBase64(reportHtml);

  await sendEdgeEmail({
    to,
    adminId,
    subject: `Shop receipt ${summary.reservationNumber}`,
    html: buildEmailBody(
      summary,
      `Here is your copy of the shop receipt for reservation <strong>${summary.reservationNumber}</strong>${
        summary.customerName ? ` (${summary.customerName})` : ''
      }.`
    ),
    attachments: [
      {
        filename: `shop-receipt-${summary.reservationNumber}.pdf`,
        content: pdfBase64,
        contentType: 'application/pdf',
        encoding: 'base64',
      },
    ],
  });
}

/**
 * Notifies the admin side, but only when something did not match.
 *
 * Never throws: the submission is already committed and visible in the report
 * before this runs, so a mail failure must not surface to the shop worker as if
 * his work were lost.
 */
export async function notifyAdminOfDiscrepancy({
  adminId,
  reportHtml,
  summary,
}: {
  adminId: string;
  reportHtml: string;
  summary: ReceiptSummaryForEmail;
}): Promise<void> {
  try {
    const totals = summariseShopReceipt(summary.lines);
    if (!totals.hasDiscrepancy) return;

    const recipients = await resolveAdminAlertRecipients(adminId);
    if (recipients.length === 0) {
      log.warn('Discrepancy recorded but no admin recipient could be resolved', { adminId });
      return;
    }

    const pdfBase64 = await htmlToPdfBase64(reportHtml);
    const [primary, ...cc] = recipients;

    await sendEdgeEmail({
      to: primary,
      cc: cc.length > 0 ? cc : undefined,
      adminId,
      subject: `Shop receipt discrepancy - ${summary.reservationNumber}`,
      html: buildEmailBody(
        summary,
        `The shop confirmed reservation <strong>${summary.reservationNumber}</strong>${
          summary.confirmedByName ? ` (checked by ${summary.confirmedByName})` : ''
        } and the count did not match what the warehouse sent.`
      ),
      attachments: [
        {
          filename: `shop-receipt-${summary.reservationNumber}.pdf`,
          content: pdfBase64,
          contentType: 'application/pdf',
          encoding: 'base64',
        },
      ],
    });
  } catch (error) {
    log.error('Failed to notify admin of shop receipt discrepancy', {
      adminId,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/** Records how the report left the terminal. Best effort: never blocks the worker. */
export async function recordShopReceiptDelivery({
  adminId,
  receiptId,
  to,
  error,
  printed,
}: {
  adminId: string;
  receiptId: string;
  to?: string | null;
  error?: string | null;
  printed?: boolean;
}): Promise<void> {
  try {
    await supabase.rpc('record_shop_receipt_delivery', {
      p_admin_id: adminId,
      p_receipt_id: receiptId,
      p_to: to ?? null,
      p_error: error ?? null,
      p_printed: Boolean(printed),
    });
  } catch (err) {
    log.warn('Could not record report delivery', {
      receiptId,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}
